import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { TimelineEquipe } from '@/services/regulationService';

/**
 * Modal d'edition d'une affectation deja planifiee (clic sur un bloc de la
 * Timeline) : permet de changer l'equipe et/ou l'heure planifiee sans passer
 * par le drag&drop. Extrait de RegulationPage.tsx pour etre reutilise par
 * RegulationTimelinePage.tsx (vue pleine largeur, timeline continue
 * multi-jours).
 */
export function EditAffectationModal({
  affectation,
  equipes,
  onClose,
  onChangeEquipe,
  onChangeHeure,
  isSavingEquipe,
  isSavingHeure,
}: {
  affectation: {
    interventionId: number;
    numeroOrdre: string;
    parking: string;
    zone: number;
    statut: string;
    currentEquipeId: number;
    heurePlanifiee: string;
    heureSouhaitee: string;
  };
  equipes: TimelineEquipe[];
  onClose: () => void;
  onChangeEquipe: (equipeId: number) => void;
  onChangeHeure: (heure: string) => void;
  isSavingEquipe: boolean;
  isSavingHeure: boolean;
}) {
  const [selectedEquipe, setSelectedEquipe] = useState(affectation.currentEquipeId);
  const [heure, setHeure] = useState(affectation.heurePlanifiee);

  const equipesCompatibles = equipes.filter((e) =>
    affectation.zone <= 4 ? e.type_engin === 'pony' : e.type_engin === 'tracteur'
  );
  const modifiable = !['cloturee', 'validee', 'annulee'].includes(affectation.statut);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Modifier la planification</h3>
          <p className="text-sm text-gray-500">
            {affectation.numeroOrdre} - {affectation.parking} - Zone {affectation.zone}
          </p>
        </div>
        <div className="px-6 py-4 space-y-4">
          {!modifiable && (
            <p className="text-sm text-amber-600">
              Cette intervention est {affectation.statut} : la planification n'est plus modifiable.
            </p>
          )}
          <div>
            <label className="label">Equipe</label>
            <select
              className="input"
              value={selectedEquipe}
              onChange={(e) => setSelectedEquipe(Number(e.target.value))}
              disabled={!modifiable}
            >
              {equipesCompatibles.map((equipe) => (
                <option key={equipe.id} value={equipe.id}>
                  {equipe.nom} ({equipe.type_engin_label}) - {equipe.charge} affectation(s)
                </option>
              ))}
            </select>
            <button
              onClick={() => onChangeEquipe(selectedEquipe)}
              disabled={!modifiable || selectedEquipe === affectation.currentEquipeId || isSavingEquipe}
              className="btn-secondary mt-2 text-sm"
            >
              {isSavingEquipe ? 'Enregistrement...' : "Changer l'equipe"}
            </button>
          </div>
          <div>
            <label className="label">Heure planifiee</label>
            <div className="flex items-center gap-2">
              <input
                type="time"
                className="input"
                value={heure}
                onChange={(e) => setHeure(e.target.value)}
                disabled={!modifiable}
              />
              <button
                onClick={() => onChangeHeure(heure)}
                disabled={!modifiable || heure === affectation.heurePlanifiee || isSavingHeure}
                className="btn-secondary text-sm whitespace-nowrap"
              >
                {isSavingHeure ? 'Enregistrement...' : 'Changer'}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-400">Heure souhaitee d'origine: {affectation.heureSouhaitee}</p>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-between items-center">
          <Link
            to={`/deneignements/${affectation.interventionId}`}
            className="text-sm text-primary-600 hover:underline"
          >
            Voir le detail complet
          </Link>
          <button onClick={onClose} className="btn-secondary">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditAffectationModal;
